import { Card, Stack, Typography, useTheme } from "@mui/material";
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useTranslation } from "react-i18next";
import IconCircleWrapper from "../../../ui/IconCircleWrapper";
import { CaretRightIcon, HomeIcon } from "../../../ui/icons";
import DynamicSvgIcon from "../../../ui/icons/DynamicSvgIcon";
import DatePickerComponent from "./DatePickerComponent";
import DescriptionField from "./DescriptionField";
import { Dayjs } from "dayjs";
import { RefObject } from "react";

interface Category {
  category: string;
  id: string;
  name: string;
  icon?: string;
  iconColor?: string;
  backgroundColor?: string;
}

interface Wallet {
  title: string;
  id: string;
  wallet: {
    id: string;
    name: string;
    balance: number;
    type: string;
  };
}

interface ExpenseTransactionFormProps {
  spendingCategory: Category | null;
  expenseWallet: Wallet | null;
  datePicker: Dayjs | undefined;
  description: string;
  openDatePicker: boolean;
  setOpenDialogSelectedExpenseCategory: (open: boolean) => void;
  setOpenDialogSelectWallets: (open: boolean) => void;
  setOpenDatePicker: (open: boolean) => void;
  setDescription: (description: string) => void;
  setDatePicker: (date: Dayjs | undefined) => void;
  descriptionInputRef?: RefObject<HTMLInputElement>;
  disableWalletSelection?: boolean;
  isBorrowExpense?: boolean;
}

const ExpenseTransactionForm = ({
  spendingCategory,
  expenseWallet,
  datePicker,
  description,
  openDatePicker,
  setOpenDialogSelectedExpenseCategory,
  setOpenDialogSelectWallets,
  setOpenDatePicker,
  setDescription,
  setDatePicker,
  disableWalletSelection,
  descriptionInputRef,
  isBorrowExpense = false
}: ExpenseTransactionFormProps) => {
  const theme = useTheme();
  const { t } = useTranslation(['common', 'transactions']);

  const openDialogSelectWallet = () => {
    if (disableWalletSelection) return;
    setOpenDialogSelectWallets(true);
  }

  return (
    <Stack spacing={4} width="100%">
      {/* Category */}
      <Stack
        direction="row"
        spacing={theme.spacing(2)}
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={theme.spacing(2)} alignItems="center">
          {spendingCategory?.icon ? (
            <Card elevation={0} sx={{ borderRadius: '50%', overflow: 'hidden', display: 'inline-block' }}>
              <DynamicSvgIcon
                svgUrl={spendingCategory.icon}
                color={spendingCategory.iconColor}
                backgroundColor={spendingCategory.backgroundColor}
                size={30}
                iconSize={18}
              />
            </Card>
          ) : (
            <IconCircleWrapper size={30} bgcolor={theme.palette.navy[800]} color={theme.palette.common.white}>
              <HomeIcon />
            </IconCircleWrapper>
          )}
        </Stack>
        {spendingCategory ? (
          <Stack
            borderBottom={`1px solid ${theme.palette.divider}`}
            width="100%"
            alignItems="end"
            direction="row"
            justifyContent="space-between"
            paddingBottom={theme.spacing(2)}
            onClick={() => setOpenDialogSelectedExpenseCategory(true)}
            sx={{ cursor: 'pointer' }}
          >
            <Typography variant="body1" color="secondary">
              {t('spending_category', { ns: 'transactions' })}
            </Typography>
            <Typography
              variant="h3"
            >
              {spendingCategory?.category} • {spendingCategory.name}
            </Typography>
          </Stack>
        ) : (
          <Stack
            borderBottom={`1px solid ${theme.palette.divider}`}
            width="100%"
            alignItems="end"
            direction="row"
            justifyContent="space-between"
            paddingBottom={theme.spacing(2)}
            onClick={() => setOpenDialogSelectedExpenseCategory(true)}
            sx={{ cursor: 'pointer' }}
          >
            <Typography variant="body1" color="secondary">
              {t('spending_category', { ns: 'transactions' })}
            </Typography>
            <CaretRightIcon style={{ fontSize: 24, color: theme.palette.text.primary }} />
          </Stack>
        )}
      </Stack>

      {/* Wallet */}
      <Stack
        direction="row"
        spacing={theme.spacing(2)}
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={theme.spacing(2)} alignItems="center">
          <IconCircleWrapper size={30} bgcolor={theme.palette.navy[800]} color={theme.palette.common.white}>
            <AccountBalanceWalletIcon sx={{ fontSize: 18 }} />
          </IconCircleWrapper>
        </Stack>
        {expenseWallet ? (
          <Stack
            borderBottom={`1px solid ${theme.palette.divider}`}
            width="100%"
            alignItems="end"
            direction="row"
            justifyContent="space-between"
            paddingBottom={theme.spacing(2)}
            onClick={openDialogSelectWallet}
            sx={{ cursor: disableWalletSelection ?  'default' : 'pointer' }}
          >
            <Typography variant="body1" color="secondary">
              {isBorrowExpense ? t('lender', { ns: 'transactions' }) : t('expense_wallet', { ns: 'transactions' })}
            </Typography>
            <Typography
              variant="h3"
            >
              {expenseWallet.wallet.name}
            </Typography>
          </Stack>
        ) : (
          <Stack
            borderBottom={`1px solid ${theme.palette.divider}`}
            width="100%"
            alignItems="end"
            direction="row"
            justifyContent="space-between"
            paddingBottom={theme.spacing(2)}
            onClick={() => setOpenDialogSelectWallets(true)}
            sx={{ cursor: 'pointer' }}
          >
            <Typography variant="body1" color="secondary">
              {isBorrowExpense ? t('lender', { ns: 'transactions' }) : t('expense_wallet', { ns: 'transactions' })}
            </Typography>
            <CaretRightIcon style={{ fontSize: 24, color: theme.palette.text.primary }} />
          </Stack>
        )}
      </Stack>

      {/* Date */}
      <DatePickerComponent
        datePicker={datePicker}
        setDatePicker={setDatePicker}
        openDatePicker={openDatePicker}
        setOpenDatePicker={setOpenDatePicker}
        clearable={false}
      />

      <DescriptionField
        description={description}
        setDescription={setDescription}
        ref={descriptionInputRef}
      />
    </Stack>
  );
};

export default ExpenseTransactionForm; 